'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import ArrowRight from '../Icons/ArrowRight';

interface FeaturedArticlesCarouselProps {
  children: React.ReactNode;
}

export const FeaturedArticlesCarousel: React.FC<FeaturedArticlesCarouselProps> = ({ children }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [thumbPos, setThumbPos] = useState(0);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);

  /* ── Sync scroll state (arrows + scrubber thumb) ── */
  const syncScroll = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const max = scrollWidth - clientWidth;
    setIsScrollable(scrollWidth > clientWidth);
    setThumbPos(max > 0 ? (scrollLeft / max) * 100 : 0);
    setCanLeft(scrollLeft > 1); // 1px threshold for floating point rounding
    setCanRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
  }, []);

  useEffect(() => {
    // Force scroll to 0 on mount to prevent browser scroll restoration
    if (sliderRef.current) {
      sliderRef.current.scrollLeft = 0;
    }

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      syncScroll();
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [children, syncScroll]);

  /* ── Scroll by 2 card widths ── */
  const scroll = (dir: 'left' | 'right') => {
    const el = sliderRef.current;
    if (!el) return;
    const cards = el.children;
    const a = cards[0] as HTMLElement | undefined;
    const b = cards[1] as HTMLElement | undefined;
    const amount = a && b ? (b.offsetLeft - a.offsetLeft) * 2 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div
      className='relative'
      style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        marginRight: 'calc(50% - 50vw)',
      }}
    >
      {/* ── Navigation arrows (lg+ only) ── */}
      {isScrollable && (
        <div className='absolute left-0 right-0 top-0 h-[242px] hidden lg:flex items-center justify-between pointer-events-none z-10 px-12'>
          <button
            onClick={() => scroll('left')}
            disabled={!canLeft}
            aria-label='Scroll left'
            className={[
              'pointer-events-auto w-12 h-12 rounded-full bg-secondary-500 text-white',
              'flex items-center justify-center transition-opacity duration-200',
              'focus-visible:outline-2 focus-visible:outline-white',
              canLeft ? 'opacity-100 cursor-pointer' : 'opacity-50 cursor-not-allowed',
            ].join(' ')}
          >
            <ArrowRight
              className='w-3 h-auto rotate-180'
              ariaHidden
              focusable={false}
            />
          </button>

          <button
            onClick={() => scroll('right')}
            disabled={!canRight}
            aria-label='Scroll right'
            className={[
              'pointer-events-auto w-12 h-12 rounded-full bg-secondary-500 text-white',
              'flex items-center justify-center transition-opacity duration-200',
              'focus-visible:outline-2 focus-visible:outline-white',
              canRight ? 'opacity-100 cursor-pointer' : 'opacity-50 cursor-not-allowed',
            ].join(' ')}
          >
            <ArrowRight
              className='w-3 h-auto'
              ariaHidden
              focusable={false}
            />
          </button>
        </div>
      )}

      {/* ── Cards scroll region ── */}
      <div
        ref={sliderRef}
        role='region'
        aria-label='Featured Articles Carousel'
        tabIndex={0}
        onScroll={syncScroll}
        className='w-full flex gap-6 lg:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 px-6 xl:px-[calc((100vw-1248px)/2)]'
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>

      {/* ── Scrubber ── */}
      {isScrollable && (
        <div
          aria-hidden='true'
          className='w-full mt-8 lg:mt-12 px-6 xl:px-0'
        >
          <div className='w-full max-w-[1248px] mx-auto relative h-[2px] rounded bg-[#e5e7eb]'>
            <div
              className='absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-secondary-500 transition-all duration-200 w-[40%] lg:w-[288px]'
              style={{
                left: isDesktop
                  ? `calc(${thumbPos}% - ${(thumbPos / 100) * 288}px)`
                  : `${thumbPos * 0.6}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
